import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ScheduleListItem } from '@futrli/views/processors/reportScheduling/reportSchedulingList';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface DenyScheduleDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  item: ScheduleListItem;
}

export const DenyScheduleDialogContainer = ({ isOpen, onClose, item }: DenyScheduleDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(
      () =>
        new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
          schedules: false,
          awaitingReview: false,
          exported: false
        }),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const { screen, handleAction: handleConfirm, handleClose } = useDialogWizardState({
    isLoading: view.state.isDenyingDispatched,
    error: view.state.errorDenyingDispatched,
    onAction: () => view.denyDispatched({ dispatchedReportId: item.sourceItem.id }),
    onClose
  });

  const title = intl.formatMessage({ id: 'reporting.cancel_send', defaultMessage: 'Cancel send' });

  const confirmDescription = intl.formatMessage({
    id: 'reporting.deny_confirm_description',
    defaultMessage: "This report will not be sent to recipients and will move to exported PDF's"
  });

  const loadingMessage = intl.formatMessage({
    id: 'reporting.cancelling',
    defaultMessage: 'Cancelling...'
  });

  const successMessage = intl.formatMessage({
    id: 'reporting.send_cancelled',
    defaultMessage: "Send cancelled and moved to exported PDF's"
  });

  const errorMessage = view.state.errorDenyingDispatched?.message;

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      screen={screen}
      title={title}
      subtitle={item.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={confirmDescription}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="deny-schedule-dialog"
    />
  );
};
